import {
  Button, Col, Modal, Popconfirm, Popover, Row,
} from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setIsShowNow, getIsPageNameEditDischarge,
  getNextAllowStatus, convertToISOStrings,
  getIsPageNameDetail,
  isToday,
} from '../../../modules/crm/inPatient/service/crmUtils';
import { RootState } from '../../../store';
import { setClaimData } from '../../../store/actions/claimDetailsAction';
import { GUTTOR_SPACE } from '../../constants/commonConstants';
import { actions, MAX_LOS_ALLOWED_WITHOUT_WARNING } from '../../constants/crmConstants';
import { setAdmissionForm, setHoursDifference, setIsShowingDoADoDEnhancementModal } from '../../../store/actions/newAdmissionAction';
import { isEmpty } from 'lodash';
import { useLocation, useHistory } from 'react-router-dom';
import DatePickerInput from '../crmFormElement/FormInputDatepicker';
import { PayerInitialState } from '../../../store/models/payerListModel';
import { parseISO, startOfDay, isBefore, isAfter, differenceInMinutes } from 'date-fns';
import DatePickerLabel from '../DatePicker/DatePickerLabel';

const HospitalizationCard = ({ form, admissionForm, actionName: claimAction, setErrorMessage }: any): ReactElement => {
  const mounted = useRef(false);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const history = useHistory();  // Using useHistory for React Router v5
  const { claimDetails, isCashlessSubmit } = useSelector((state: RootState): any => state?.admissionDetails || {});
  const { policyStartDate, policyEndDate } = useSelector((state: RootState): any => state?.admissionDetails?.claimDetails || {});
  const {
    payer: { isCorporatePayer, shortName },
  } = useSelector((state: RootState): any => state?.payerList || {});
  const { isEasyDischargeCase } = useSelector((state: RootState): any => state.selectedInPatientDetail);
  const { hospitalSearchPatientDetails } = useSelector((state: RootState): any => state.hospital);
  const [isLOSgtWarning, setIsLOSgtWarning] = useState(false);
  const [popConfirmCancel, setPopConfirmCancel] = useState(false);
  const [enhancementFlowContinuePopUp, setEnhancementFlowContinueVisible] = useState(false);
  const [dateDifference, setDateDifference] = useState(0);
  const { payer, formControlConfig } = useSelector((state: RootState): PayerInitialState => state.payerList);
  const { admissionFormVal } = useSelector((state: RootState): any => state?.newAdmission);
  const commonDetail = { form };
  const payerConfigValues = Object.values(formControlConfig);
  const isDischargePage = getIsPageNameEditDischarge(pathname);
  const isDisableDODRequired = payerConfigValues?.some(({ disableDODSelection }: any): boolean =>
    disableDODSelection) && isDischargePage;
  const {newAdmission: { isDischargeCase}} = useSelector((state: RootState): RootState => state);

  const checkInValidDod = (admissionDate: string, dischargeDate: string): boolean => {
    // function to check if DOD is not greater than DOA by atleast 30 mins or
    // If DOD is gretaer than today if its discharge case
    const formattedDischargeDate = new Date(dischargeDate);
    const formattedAdmissionDate = new Date(admissionDate);
    const today = new Date();
    return differenceInMinutes(formattedDischargeDate, formattedAdmissionDate) < 30
      || (isDischargePage && (isAfter(formattedDischargeDate, today)));
  };

  const checkInValidDoa = (admissionDate: string): boolean => {
    // to prevent DOA prefill incase of Discharge flow if DOA is greater than current date
    const formattedAdmissionDate = new Date(admissionDate);
    const today = new Date();
    return (isDischargePage && (isAfter(formattedAdmissionDate, today)));
  };

  useEffect((): any => {
    mounted.current = true;
    const formValues = form.getFieldsValue(['stayLength']);
    const dateRange = convertToISOStrings(formValues?.stayLength);
    const [doaISO, dodISO] = dateRange || [];
    const doa = parseISO(doaISO);
    const dod = parseISO(dodISO);
    if (doa && dod) {
      setDateDifference(dateDifference);
    }
    dispatch(setIsShowingDoADoDEnhancementModal(true));
    return (): boolean => (mounted.current = false);

  }, []);

  useEffect((): any => {
    const treatmentDetails = hospitalSearchPatientDetails?.[0]?.treatmentDetails;
    if (isEmpty(treatmentDetails)) {
      return;
    }
    const { dateOfAdmission, dateOfDischarge } = treatmentDetails;
    let modifiedDateOfDischarge = dateOfDischarge;
    let modifiedDateOfAdmission = dateOfAdmission;

    if (dateOfDischarge && (checkInValidDod(dateOfAdmission, dateOfDischarge)) || isDisableDODRequired) {
      // to prevent prefill of DOD if DOD is invalid
      modifiedDateOfDischarge = null;
    }
    if (dateOfAdmission && checkInValidDoa(dateOfAdmission)) {
      // to prevent prefill of DOA if DOA is invalid
      modifiedDateOfAdmission = null;
    }
    const stayLength: any = [modifiedDateOfAdmission, modifiedDateOfDischarge];
    const targetField = payer?.isOnMailPayer ? 'customerDetail' : 'treatmentDetail';
    const newFormVal = {
      ...admissionFormVal,
      [targetField]: {
        ...admissionFormVal[targetField],
        stayLength,
      },
    };
    dispatch(setAdmissionForm(newFormVal));
  }, [hospitalSearchPatientDetails, isDischargePage]);

  const clearValidationError = (fieldName: any): any => {
    // Clear validation error for the specified field
    form?.setFields([{ name: fieldName, errors: [] }]);
  };

    const handleOk = (): void => {
    const currentPathname = window.location.pathname; // Get current pathname
    const newPathname = currentPathname.replace('/edit', '/discharge'); // Replace "edit" with discharge to continue discharge flow"
    history.push(newPathname);
    setEnhancementFlowContinueVisible(false);
  };

  const onContinue = (): void => {
    setEnhancementFlowContinueVisible(false);
  };

  useEffect((): any => {
    if ((isDischargeCase) && form.getFieldValue(['stayLength'])) {
      form?.validateFields(['stayLength']);
    } else {
      clearValidationError(['stayLength']);
    }
  }, [isDischargeCase]);

  const checkShowNow = (): any => {
    return (typeof policyEndDate === 'string' && policyEndDate?.length > 0) ?
      setIsShowNow(new Date(policyStartDate), new Date(policyEndDate)) : true;
  };
  useEffect((): void => {
    if (dateDifference > 0) {
      setErrorMessage('');
      const formValues = form.getFieldsValue(['stayLength']);
      if (!formValues?.stayLength) {
        return;
      }
      const dateRange = convertToISOStrings(formValues?.stayLength);
      const [doa, dod] = dateRange || [];
      if (!doa || !dod) {
        return;
      } else {
        if (shortName === 'HDFC') {
          dispatch(setHoursDifference(Math?.floor(dateDifference) + 1));
        }
        form?.validateFields(['stayLength']);
        if (dateDifference > MAX_LOS_ALLOWED_WITHOUT_WARNING) {
          setIsLOSgtWarning(true);
          setPopConfirmCancel(false);
        } else {
          setIsLOSgtWarning(false);
        }
      }
      if (dod) {
        const { allowStatus } = claimDetails;
        const isIntegratedPayer = claimDetails?.payerType === 'IntegrationPayer';
        const [{ documentName = [], actionName = null } = {}] =
          getNextAllowStatus(allowStatus, dod, claimAction, isCorporatePayer, false, isEasyDischargeCase, pathname, isIntegratedPayer);
        claimDetails['nextAllowStatus'] = actionName ? [actionName] : [];
        const isDischargeFlowPresent = allowStatus.some(({ actionName : allowedAction}: any): boolean =>
          allowedAction === (actions.DISCHARGE || actions.DISCHARGECONTEST));
        const confirmStatus = actionName === 'ENHANCEMENT';
        claimDetails['mandatoryClassificationName'] = documentName;
        claimDetails['doa'] = claimDetails['dateOfAdmission'] = doa;
        claimDetails['dod'] = claimDetails['dateOfDischarge'] = dod;
        claimDetails['stayLength'] = [doa, dod];
        if (mounted.current) {
          dispatch(setClaimData({ ...claimDetails }));
        }
        if ((isBefore(new Date(dod), startOfDay(new Date())) ||
          isToday(dod)) && confirmStatus && isDischargeFlowPresent) {
          // the modal opens for Integrated Payers, if dod is less than or equal to today
          // and if Discharge flow is Present for that particular status
          setEnhancementFlowContinueVisible(true);
        }
      }
      if (doa && dod && isCashlessSubmit) {
        const todayDate = startOfDay(new Date());
        if (isBefore(new Date(dod), todayDate)) {
          setErrorMessage(' ');
        } else {
          setErrorMessage('Data shows that the patient is still in the hospital, update DOD for initiating settlement or click on "Edit" to enhance a claim request');
        }
      }
    }
  }, [dateDifference]);

  const setSelectedTime = (admitDate: any, dischargeDate: any, admitTime: any, dischargeTime: any): void => {
    form?.setFieldsValue({ stayLength: [admitDate?.concat(' ', admitTime), dischargeDate?.concat(' ', dischargeTime)] });
    // When time is changed the form fields are not set with the changed time (Email+IPP+RPA)
    const admitDateTime = new Date(`${admitDate} ${admitTime}`);
    const dischargeDateTime = new Date(`${dischargeDate} ${dischargeTime}`);
    const minutesDifference = differenceInMinutes(dischargeDateTime, admitDateTime);
    const daysDifference = Math.ceil(minutesDifference / (24 * 60));
    setDateDifference(daysDifference);
  };

  const getModalContent = (): ReactElement => {
    return (<div className="confirmFlowModal">
      <p className="confirmText">
        Selected DOD is of today or a past date. If submitting a discharge case, please use the <b> 'Discharge' </b> button
      </p>
      <Row gutter={GUTTOR_SPACE} className="row-cont">
        <Col>
          <Button className="cancelBtn" size="small" onClick={onContinue}>Continue</Button>
        </Col>
        <Col>
          <Button type="primary" size="small" className="dischargeBtn" onClick={handleOk}>Go to Discharge</Button>
        </Col>
      </Row>
    </div>
    );
  };
  return (
    <>
      <Row gutter={GUTTOR_SPACE} >
        <Popconfirm
          placement="topRight"
          title={`The length of stay selected is for ${dateDifference} days. Is it correct?`}
          okText="Yes"
          cancelText="No"
          visible={isLOSgtWarning}
          onCancel={(): any => {
            setPopConfirmCancel(true);
            setIsLOSgtWarning(false);
          }}
          onConfirm={(): any => setIsLOSgtWarning(false)}
        >
          <DatePickerInput detail={{
            ...commonDetail,
            key: 'stayLength',
            size: 15,
            setval: (admitDate: any, dischargeDate: any, admitTime: any, dischargeTime: any): any => {
              setSelectedTime(admitDate, dischargeDate, admitTime, dischargeTime);
            },
            setDifference: (noOfDays: number): any => { setDateDifference(noOfDays); },
            showNow: isEasyDischargeCase ? false : checkShowNow(),
            policyStartDate: claimDetails.policyStartDate,
            policyEndDate: claimDetails.policyEndDate,
            isDischargeCase: getIsPageNameEditDischarge(pathname),
            isHospitalizationCard: true,
            isEasyDischargeCase,
            popConfirmCancel,
            claimForm: form,
            isDetailPage: getIsPageNameDetail(pathname),
            isContinuePopVisible: enhancementFlowContinuePopUp,
            lOSgtWarning: isLOSgtWarning,
            isShowSuggestedValue: true,
            disableDOD: isDisableDODRequired,
          }} />
        </Popconfirm>
        <Col>
          <Row className="inpatient">
            <Col className="info-circle">

              <Popover
                content={'Inpatient | Daycare: Based on DOA & DOD'}
                overlayClassName="custom-tooltip-datepicker"
                placement="bottom"
              >
                <InfoCircleFilled />
              </Popover>
            </Col>
            <DatePickerLabel dateDifference={dateDifference}/>
          </Row>
        </Col>
        {/* { getIsPageNameEditDischarge(pathname) && <span className="date_of_discharge_note">    // will be uncomment or remove later
              Date of Discharge cannot be edited. Please click on Next to Initiate Discharge</span>} */}
      </Row>
      <Modal
        visible={enhancementFlowContinuePopUp && !isLOSgtWarning}
        footer={false}
        closable={false}>
        {getModalContent()}
      </Modal>
    </>
  );
};

export default HospitalizationCard;
